import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', form);
      setSent(true);
      toast.success('Message envoyé avec succès');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-24 md:py-32 bg-background" data-testid="contact-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">Contactez-nous</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Notre équipe est à votre écoute pour répondre à toutes vos questions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {[
              { icon: Mail, title: 'Email', text: 'contact@m-carelink.com', sub: 'Réponse sous 24h' },
              { icon: Phone, title: 'Téléphone', text: '01 23 45 67 89', sub: 'Lun-Ven, 9h-18h' },
              { icon: MapPin, title: 'Adresse', text: 'Paris, France', sub: 'Sur rendez-vous' },
              { icon: Clock, title: 'Horaires', text: 'Lundi au vendredi', sub: '9h00 - 18h00' },
            ].map(({ icon: Icon, title, text, sub }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl bg-white border border-border/30">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{title}</p>
                  <p className="text-foreground">{text}</p>
                  <p className="text-xs text-muted-foreground">{sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div className="bg-white rounded-2xl p-10 text-center border border-border/50 shadow-sm">
                <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-secondary" />
                </div>
                <h2 className="font-heading text-2xl font-semibold mb-2">Message envoyé !</h2>
                <p className="text-muted-foreground">Notre équipe vous répondra dans les plus brefs délais.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-border/50 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom complet *</Label>
                    <Input value={form.name} onChange={e => u('name', e.target.value)} required className="mt-1 rounded-xl" data-testid="contact-name" />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={form.email} onChange={e => u('email', e.target.value)} required className="mt-1 rounded-xl" data-testid="contact-email" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Téléphone</Label>
                    <Input type="tel" value={form.phone} onChange={e => u('phone', e.target.value)} className="mt-1 rounded-xl" data-testid="contact-phone" />
                  </div>
                  <div>
                    <Label>Sujet</Label>
                    <select value={form.subject} onChange={e => u('subject', e.target.value)} className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" data-testid="contact-subject">
                      <option value="">Sélectionnez...</option>
                      <option value="information">Demande d'information</option>
                      <option value="sejour">Question sur un séjour</option>
                      <option value="partenariat">Partenariat</option>
                      <option value="b2b">Offre entreprise/mutuelle</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Message *</Label>
                  <Textarea value={form.message} onChange={e => u('message', e.target.value)} required placeholder="Votre message..." className="mt-1 rounded-xl min-h-[150px]" data-testid="contact-message" />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary text-primary-foreground py-5 font-semibold hover:bg-primary/90 transition-all" data-testid="contact-submit-btn">
                  {loading ? 'Envoi...' : 'Envoyer le message'} <Send className="ml-2 w-4 h-4" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
